import { useEffect, useState, useMemo } from 'react';
import { motion } from 'framer-motion';
import { stagger, fadeUp } from '@/lib/animations';
import {
  Plus,
  MoreHorizontal,
  Pencil,
  Trash2,
  Unlock,
  Key,
  Users as UsersIcon,
  UserCheck,
  Shield,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { toast } from 'sonner';
import {
  PageHeader,
  TableFilters,
  DataTable,
  Column,
  StatusBadge,
  ConfirmDialog,
  MetricCard,
  KPIGrid,
} from '@/components/common';
import { usersApi, User, QueryUserParams } from '@/api/services';
import { getErrorMessage } from '@/api/client';
import { formatDate } from '@/lib/utils';

export default function Users() {
  const [users, setUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [deleteDialog, setDeleteDialog] = useState<{ open: boolean; user?: User }>({
    open: false,
  });
  const [deleting, setDeleting] = useState(false);

  const fetchUsers = async (params?: QueryUserParams) => {
    try {
      setLoading(true);
      const response = await usersApi.getAll(params);
      setUsers(response.data);
    } catch (error) {
      toast.error(getErrorMessage(error));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchUsers({ search: searchQuery || undefined });
  }, [searchQuery]);

  const handleDelete = async () => {
    if (!deleteDialog.user) return;

    try {
      setDeleting(true);
      await usersApi.delete(deleteDialog.user.id);
      toast.success('User deactivated successfully');
      setDeleteDialog({ open: false });
      fetchUsers({ search: searchQuery || undefined });
    } catch (error) {
      toast.error(getErrorMessage(error));
    } finally {
      setDeleting(false);
    }
  };

  const handleUnlock = async (user: User) => {
    try {
      await usersApi.unlockAccount(user.id);
      toast.success('Account unlocked successfully');
      fetchUsers({ search: searchQuery || undefined });
    } catch (error) {
      toast.error(getErrorMessage(error));
    }
  };

  const getInitials = (name: string) => {
    return name
      .split(' ')
      .map((n) => n[0])
      .join('')
      .toUpperCase()
      .slice(0, 2);
  };

  const metrics = useMemo(() => {
    const total = users.length;
    const active = users.filter((u) => u.status === 'ACTIVE').length;
    const adminUsers = users.filter((u) =>
      u.userRoles.some((ur) => ur.role.code === 'ADMIN' || ur.role.code === 'SUPER_ADMIN')
    ).length;

    return { total, active, adminUsers };
  }, [users]);

  const columns: Column<User>[] = [
    {
      key: 'fullName',
      header: 'User',
      sortable: true,
      render: (user) => (
        <div className="flex items-center gap-3">
          <Avatar className="h-9 w-9">
            <AvatarFallback className="bg-primary/10 text-primary text-xs">
              {getInitials(user.fullName)}
            </AvatarFallback>
          </Avatar>
          <div>
            <div className="font-medium">{user.fullName}</div>
            <div className="text-xs leading-4 text-muted-foreground">{user.email}</div>
          </div>
        </div>
      ),
    },
    {
      key: 'department',
      header: 'Department',
      render: (user) => <span className="text-muted-foreground">{user.department || '-'}</span>,
    },
    {
      key: 'userRoles',
      header: 'Roles',
      render: (user) => (
        <div className="flex flex-wrap gap-1">
          {user.userRoles.slice(0, 2).map((ur) => (
            <Badge key={ur.id} variant="secondary" className="text-xs">
              {ur.role.name}
            </Badge>
          ))}
          {user.userRoles.length > 2 && (
            <Badge variant="outline" className="text-xs">
              +{user.userRoles.length - 2}
            </Badge>
          )}
        </div>
      ),
    },
    {
      key: 'status',
      header: 'Status',
      render: (user) => <StatusBadge status={user.status.toLowerCase() as any} />,
    },
    {
      key: 'lastLoginAt',
      header: 'Last Login',
      render: (user) => (user.lastLoginAt ? formatDate(user.lastLoginAt) : 'Never'),
    },
    {
      key: 'actions',
      header: '',
      className: 'w-12',
      render: (user) => (
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="ghost" size="icon" aria-label="More actions" className="h-8 w-8">
              <MoreHorizontal className="h-4 w-4" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            <DropdownMenuItem>
              <Pencil className="mr-2 h-4 w-4" />
              Edit
            </DropdownMenuItem>
            <DropdownMenuItem>
              <Key className="mr-2 h-4 w-4" />
              Reset Password
            </DropdownMenuItem>
            {user.status === 'LOCKED' && (
              <DropdownMenuItem onClick={() => handleUnlock(user)}>
                <Unlock className="mr-2 h-4 w-4" />
                Unlock Account
              </DropdownMenuItem>
            )}
            <DropdownMenuSeparator />
            <DropdownMenuItem
              className="text-destructive"
              onClick={() => setDeleteDialog({ open: true, user })}
            >
              <Trash2 className="mr-2 h-4 w-4" />
              Deactivate
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      ),
    },
  ];

  return (
    <motion.main className="space-y-5" variants={stagger} initial="hidden" animate="show">
      <motion.div variants={fadeUp}>
        <PageHeader
          title="Users"
          description="Accounts with access to the platform, with assigned roles, department, and last sign-in."
          actions={
            <Button>
              <Plus className="mr-2 h-4 w-4" />
              Add User
            </Button>
          }
        />
      </motion.div>

      <div>
        <KPIGrid className="mt-6">
          <MetricCard
            label="Total Users"
            value={metrics.total}
            sentiment="info"
            icon={<UsersIcon className="h-5 w-5" />}
          />
          <MetricCard
            label="Active Users"
            value={metrics.active}
            sentiment="success"
            icon={<UserCheck className="h-5 w-5" />}
          />
          <MetricCard
            label="Admin Users"
            value={metrics.adminUsers}
            sentiment="warning"
            icon={<Shield className="h-5 w-5" />}
          />
        </KPIGrid>
      </div>

      <motion.div variants={fadeUp} className="mt-6">
        <DataTable
          toolbar={
            <TableFilters
              search={searchQuery}
              onSearchChange={setSearchQuery}
              searchPlaceholder="Search users..."
            />
          }
          data={users}
          columns={columns}
          loading={loading}
          pagination
          exportable
          columnToggle
          emptyTitle="No users found"
          emptyDescription="Get started by inviting your first user."
          emptyAction={
            <Button>
              <Plus className="mr-2 h-4 w-4" />
              Add User
            </Button>
          }
        />
      </motion.div>

      <ConfirmDialog
        open={deleteDialog.open}
        onOpenChange={(open) => setDeleteDialog({ open })}
        title="Deactivate User"
        description={`Are you sure you want to deactivate "${deleteDialog.user?.fullName}"? They will no longer be able to access the system.`}
        confirmLabel="Deactivate"
        onConfirm={handleDelete}
        variant="destructive"
        loading={deleting}
      />
    </motion.main>
  );
}
